import React, { useState, useEffect } from "react"
import styled from "styled-components"
import {
  Modal, Input, Select, Switch, Button,
} from "antd"
import { PlusOutlined } from "@ant-design/icons"
import aiContainer from "../../../../container/AI"

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const PlusIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #100063;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const ModalTitle = styled.span`
  font-size: 18px;
  font-weight: 500;
`

const FormLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.85);
`

const TeamAssignmentSection = styled.div`
  background: #E6F4FF;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`

const TeamAssignmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`

const OptionalPill = styled.span`
  font-size: 12px;
  color: #8A8A8A;
  background: #F5F5F5;
  padding: 2px 8px;
  border-radius: 4px;
`

const TeamToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${props => props.hasDropdown ? "12px" : "0"};
`

const SaveDocumentButton = styled(Button)`
  &.ant-btn-primary[disabled],
  &.ant-btn-primary.ant-btn-disabled {
    color: rgba(0, 0, 0, 0.65) !important;
    background: #f5f5f5 !important;
    border-color: #d9d9d9 !important;
  }
`

const NY_SUB_TYPE_OPTIONS = [
  { value: "one_key_mls", label: "One key MLS" },
  { value: "brooklyn_mls", label: "Brooklyn MLS" },
  { value: "rls", label: "RLS" },
  { value: "other", label: "Other" },
]

const TC_STATE = [
  { label: "California", value: "california" },
  { label: "Colorado", value: "colorado" },
  { label: "Texas", value: "texas" },
  { label: "Florida", value: "florida" },
  { label: "Washington", value: "washington" },
  { label: "Georgia", value: "georgia" },
  { label: "Oregon", value: "oregon" },
  { label: "Arizona", value: "arizona" },
  { label: "New York", value: "newyork" },
  { label: "All State", value: "All State" },
]

const AddDocumentModal = ({
  visible,
  onClose,
  onSave,
  defaultState = "california",
  entityName = "common_entity_name",
  entityType = "common_entity_type",
  fetchComplianceTeams,
  fetchComplianceTeamsResponse,
}) => {
  const [documentTitle, setDocumentTitle] = useState("")
  const [selectedState, setSelectedState] = useState(defaultState)
  const [templateId, setTemplateId] = useState("")
  const [assignToTeam, setAssignToTeam] = useState(false)
  const [teamId, setTeamId] = useState(undefined)
  const [nySubType, setNySubType] = useState("one_key_mls")
  const [saving, setSaving] = useState(false)

  const teams = fetchComplianceTeamsResponse?.data || []
  const teamsLoading = fetchComplianceTeamsResponse?.isFetching || false

  useEffect(() => {
    if (visible) {
      fetchComplianceTeams()
    }
  }, [visible])

  useEffect(() => {
    if (visible) {
      setDocumentTitle("")
      setTemplateId("")
      setAssignToTeam(false)
      setTeamId(undefined)
      setSelectedState(defaultState)
      setNySubType("one_key_mls")
    }
  }, [visible, defaultState])

  useEffect(() => {
    if (selectedState === "newyork") {
      setNySubType("one_key_mls")
    }
  }, [selectedState])

  const handleSave = () => {
    if (!documentTitle?.trim()) return
    if (!templateId?.trim()) return

    const payload = {
      idToAdd: `new-temp-${Date.now()}`,
      name: documentTitle.trim(),
      templateId: templateId.trim(),
      entityName,
      entityType,
      state: selectedState === "newyork" ? "New York" : selectedState,
    }

    if (selectedState === "newyork") {
      payload.nySubType = nySubType
    }

    if (assignToTeam && teamId) {
      payload.teamId = teamId
    }

    setSaving(true)
    onSave(payload, () => {
      setSaving(false)
      onClose()
    })
  }

  const isValid = documentTitle?.trim() && templateId?.trim()

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      closeIcon={<span style={{ fontSize: 18 }}>×</span>}
      title={(
        <ModalHeader>
          <PlusIcon>
            <PlusOutlined style={{ fontSize: 16 }} />
          </PlusIcon>
          <ModalTitle>Add New Document</ModalTitle>
        </ModalHeader>
      )}
    >
      <div style={{ marginBottom: 16 }}>
        <FormLabel>Document Title *</FormLabel>
        <Input
          placeholder="Enter document title"
          value={documentTitle}
          onChange={e => setDocumentTitle(e.target.value)}
          style={{
            width: "100%",
            height: 32,
            background: "#F9F8FF",
            borderColor: "#D9D9D9",
          }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <FormLabel>State *</FormLabel>
        <Select
          placeholder="Select state"
          style={{ width: "100%", height: 32 }}
          value={selectedState}
          onChange={setSelectedState}
          options={TC_STATE}
        />
      </div>

      {selectedState === "newyork" && (
        <div style={{ marginBottom: 16 }}>
          <FormLabel>Sub Type</FormLabel>
          <Select
            placeholder="Select sub type"
            style={{ width: "100%", height: 32 }}
            value={nySubType}
            onChange={setNySubType}
            options={NY_SUB_TYPE_OPTIONS}
          />
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <FormLabel>Template ID *</FormLabel>
        <Input
          placeholder="Enter template ID"
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
          style={{
            width: "100%",
            height: 32,
            background: "#F9F8FF",
            borderColor: "#D9D9D9",
          }}
        />
      </div>

      <TeamAssignmentSection>
        <TeamAssignmentHeader>
          <FormLabel style={{ marginBottom: 0 }}>Team Assignment</FormLabel>
          <OptionalPill>Optional</OptionalPill>
        </TeamAssignmentHeader>
        <TeamToggleRow hasDropdown={assignToTeam}>
          <Switch
            checked={assignToTeam}
            onChange={setAssignToTeam}
          />
          <span style={{ fontSize: 14 }}>Assign this document to a team</span>
        </TeamToggleRow>
        {assignToTeam && (
          <Select
            placeholder="Select team"
            style={{ width: "100%", height: 32 }}
            value={teamId}
            onChange={setTeamId}
            loading={teamsLoading}
            allowClear
            options={teams.map(team => ({
              value: team.team_id,
              label: team.team_name,
            }))}
          />
        )}
      </TeamAssignmentSection>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <SaveDocumentButton
          type="primary"
          onClick={handleSave}
          disabled={!isValid || saving}
          loading={saving}
          style={{ background: "#100063", borderColor: "#100063" }}
        >
          Save Document
        </SaveDocumentButton>
      </div>
    </Modal>
  )
}

export default aiContainer(AddDocumentModal)
export { TC_STATE }
